import { useRef, useState, useMemo, useEffect } from 'react';
import { Button, Field, Input, makeStyles, tokens } from '@fluentui/react-components';
import type { CatalogItem } from '../../../models/CatalogItem';

const useStyles = makeStyles({
  wrapper: { position: 'relative', flex: '1', zIndex: 1000 },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    maxHeight: '280px',
    overflowY: 'auto',
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    zIndex: 9999,
    marginTop: '2px',
  },
  option: {
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '13px',
    ':hover': { background: tokens.colorNeutralBackground1Hover },
  },
  optionSelected: {
    background: tokens.colorBrandBackground2,
  },
  price: { marginLeft: '8px', color: tokens.colorNeutralForeground3 },
  empty: { padding: '8px 12px', fontSize: '13px', color: tokens.colorNeutralForeground3 },
});

export const ProductPicker = ({ items, selectedSku, onSelect, onAdd }: {
  items: CatalogItem[];
  selectedSku: string;
  onSelect: (value: string) => void;
  onAdd: () => void;
}) => {
  const styles = useStyles();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (q.length < 2) return [];
    return items
      .filter(
        (item) =>
          item.cr_crm_productname.toLowerCase().includes(q) ||
          item.cr_crm_skuname.toLowerCase().includes(q) ||
          item.cr_crm_skuid.toLowerCase().includes(q),
      )
      .sort((a, b) => a.cr_crm_skuid.localeCompare(b.cr_crm_skuid, undefined, { numeric: true }))
      .slice(0, 50);
  }, [items, query]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    setOpen(val.trim().length >= 2);
    if (selectedSku) onSelect('');
  };

  const handleSelect = (item: CatalogItem) => {
    onSelect(item.cr_crm_catalogcacheid);
    setQuery(`${item.cr_crm_productname} — ${item.cr_crm_skuname}`);
    setOpen(false);
  };

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'end' }}>
      <div className={styles.wrapper} ref={wrapperRef}>
        <Field label="Product catalog">
          <Input
            placeholder="Type at least 2 chars to search (e.g. E3, Business Basic...)"
            value={query}
            onChange={handleInputChange}
            onFocus={() => { if (query.trim().length >= 2 && !selectedSku) setOpen(true); }}
          />
        </Field>
        {open && (
          <div className={styles.dropdown}>
            {filtered.length === 0 ? (
              <div className={styles.empty}>No results for "{query}"</div>
            ) : (
              filtered.map((item) => (
                <div
                  key={item.cr_crm_catalogcacheid}
                  className={`${styles.option} ${item.cr_crm_catalogcacheid === selectedSku ? styles.optionSelected : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(item)}
                >
                  <strong>{item.cr_crm_productname}</strong>
                  {item.cr_crm_skuname ? ` — ${item.cr_crm_skuname}` : ''}
                  {item.cr_crm_skuid ? ` — SKU: ${item.cr_crm_skuid}` : ''}
                  <br />
                  <span style={{ color: tokens.colorNeutralForeground3, fontSize: '12px' }}>
                    {item.cr_crm_category}
                    {(() => {
                      const price = item.cr_crm_erppriceusd;
                      const billing = String(item.cr_crm_billingcycles ?? '').toLowerCase();
                      const suffix = billing === 'annual' ? '/yr' : billing === 'triennial' ? '/3yr' : billing === 'monthly' ? '/mo' : '';
                      if (price > 0) return `  $${price.toFixed(2)}${suffix}`;
                      if (price === 0) return '  Free';
                      return '';
                    })()}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      <Button appearance="primary" onClick={onAdd} disabled={!selectedSku}>Add product</Button>
    </div>
  );
};
